import React from "react";
import { ArrowUpRight, ShieldCheck, GitBranch, ListChecks, BarChart3, GraduationCap, FileSearch } from "lucide-react";

const services = [
  {
    n: "01",
    icon: ShieldCheck,
    title: "EHS Systems & Governance",
    body:
      "Workplace safety programs, SOPs, control frameworks, permit-to-work systems and operational health checks tuned to your site reality.",
    items: ["HIRA · HAZOP · JHA", "Permit-to-Work (PTW)", "Behavioural safety culture"],
  },
  {
    n: "02",
    icon: GitBranch,
    title: "SAP S/4HANA EHS",
    body:
      "Workplace Safety module implementation: BGM, SVT, GLM and IDoc coordination, process mapping, rollout and stabilization.",
    items: ["Incident reporting workflows", "Hazardous substance tracking", "Audit documentation"],
  },
  {
    n: "03",
    icon: FileSearch,
    title: "Risk & Compliance",
    body:
      "ISO 45001 / 14001 / 9001 lead auditing, NEBOSH/IOSH-aligned compliance, ESG advisory and internal review readiness.",
    items: ["Lead audit prep", "ESG & carbon reporting", "Compliance trackers"],
  },
  {
    n: "04",
    icon: BarChart3,
    title: "Reporting & Power BI Dashboards",
    body:
      "Leading & lagging indicator dashboards, management review metrics, action trackers and incident/observation visibility.",
    items: ["LTIFR & TRIR tracking", "Executive dashboards", "Action closure analytics"],
  },
  {
    n: "05",
    icon: ListChecks,
    title: "Incident & MOC Management",
    body:
      "RCA, CAPA programs and structured Management of Change reviews to systematically prevent recurrence.",
    items: ["Root cause analysis", "CAPA closure", "MOC governance"],
  },
  {
    n: "06",
    icon: GraduationCap,
    title: "Training & Capability",
    body:
      "Site-specific safety inductions, toolbox talks, HAZMAT handling, PTW, emergency response and contractor briefings.",
    items: ["200+ sessions / yr", "Multilingual delivery", "Measurable outcomes"],
  },
];

export default function Services() {
  return (
    <section
      data-testid="services"
      id="services"
      className="relative bg-white border-t border-line"
    >
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-20 lg:py-28">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-14">
          <div className="lg:col-span-5">
            <p className="mono-label">Practice — 06 service lines</p>
            <h2 className="font-display text-4xl md:text-5xl lg:text-6xl uppercase tracking-tight mt-5 leading-[0.95]">
              Built for sites that
              <br />
              cannot afford to fail
              <span className="text-accent-red">.</span>
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 flex flex-col justify-end">
            <p className="text-ink/70 text-base md:text-lg leading-relaxed">
              Each engagement starts with a walk-the-floor diagnosis, ends
              with measurable controls, and lives on as documented,
              audit-ready governance. No template-thinking — every system is
              calibrated to the operational reality of the site.
            </p>
          </div>
        </div>

        <div
          data-testid="services-grid"
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 border-t border-l border-line"
        >
          {services.map((s) => {
            const Icon = s.icon;
            return (
              <article
                key={s.n}
                data-testid={`service-card-${s.n}`}
                className="group relative p-8 lg:p-10 border-r border-b border-line bg-white hover:bg-bgalt transition-colors min-h-[340px] flex flex-col"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-xs text-ink-muted">
                    [{s.n}]
                  </span>
                  <ArrowUpRight
                    size={20}
                    className="opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all"
                  />
                </div>
                <Icon size={28} strokeWidth={1.4} className="text-accent-red mb-6" />
                <h3 className="font-display text-2xl uppercase tracking-tight leading-tight">
                  {s.title}
                </h3>
                <p className="mt-3 text-sm text-ink/70 leading-relaxed">
                  {s.body}
                </p>
                <ul className="mt-auto pt-6 space-y-1.5">
                  {s.items.map((it) => (
                    <li
                      key={it}
                      className="font-mono text-[12px] text-ink-muted flex items-center gap-2"
                    >
                      <span className="w-3 h-px bg-ink-muted inline-block"></span>
                      {it}
                    </li>
                  ))}
                </ul>
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
